import { useEffect, useRef, useState } from "react";

// Video do YouTube sem a "moldura" do YouTube (titulo, canal, compartilhar, logo,
// "Mais videos"). O embed nao deixa desligar isso por parametro, entao:
// - o iframe e 180px mais alto e sobe 90px: as barras de cima e de baixo ficam
//   fora da area visivel (overflow: hidden no pai);
// - controls=0 e um botao transparente por cima recebem os cliques, e
//   play/pause/seek vao pelo postMessage da IFrame API (enablejsapi=1);
// - tempo e duracao chegam pelo mesmo canal (eventos infoDelivery), e a barra
//   de progresso e a tela cheia sao nossas, no container, para continuar recortado.
// Mesma tecnica da vitrine (vitrine.newcortex.site/radar-de-precos).
// Ocupa o pai inteiro: o pai precisa ser position: relative com altura.

const ORIGEM_YOUTUBE = "https://www.youtube-nocookie.com";

function enviar(iframe, mensagem) {
  iframe?.contentWindow?.postMessage(JSON.stringify(mensagem), ORIGEM_YOUTUBE);
}

function comando(iframe, func, args = []) {
  enviar(iframe, { event: "command", func, args });
}

function formatarTempo(segundos) {
  const s = Math.max(0, Math.floor(segundos || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const VELOCIDADES = [0.5, 0.75, 1, 1.25, 1.5, 2];

const telaCheiaDisponivel =
  typeof document !== "undefined" && !!document.documentElement.requestFullscreen;

function IconeSom({ mudo }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      {mudo ? (
        <>
          <line x1="22" y1="9" x2="16" y2="15" />
          <line x1="16" y1="9" x2="22" y2="15" />
        </>
      ) : (
        <>
          <path d="M15.5 8.5a5 5 0 0 1 0 7" />
          <path d="M19 5a10 10 0 0 1 0 14" />
        </>
      )}
    </svg>
  );
}

export default function VideoYouTube({ id, titulo }) {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const arrastandoRef = useRef(false);
  const [iniciado, setIniciado] = useState(false);
  const [tocando, setTocando] = useState(false);
  const [tempo, setTempo] = useState(0);
  const [duracao, setDuracao] = useState(0);
  const [volume, setVolume] = useState(100);
  const [mudo, setMudo] = useState(false);
  const [velocidade, setVelocidade] = useState(1);

  useEffect(() => {
    if (!iniciado) return undefined;
    const aoReceber = (e) => {
      if (e.origin !== ORIGEM_YOUTUBE || e.source !== iframeRef.current?.contentWindow) return;
      let dados;
      try {
        dados = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }
      const info = dados?.info;
      if (!info || typeof info !== "object") return;
      if (typeof info.duration === "number" && info.duration > 0) setDuracao(info.duration);
      if (typeof info.currentTime === "number" && !arrastandoRef.current) setTempo(info.currentTime);
      // 1 = tocando; 2 = pausado; 0 = terminou
      if (info.playerState === 1) setTocando(true);
      else if (info.playerState === 2 || info.playerState === 0) setTocando(false);
    };
    window.addEventListener("message", aoReceber);
    return () => window.removeEventListener("message", aoReceber);
  }, [iniciado]);

  const escutar = () => enviar(iframeRef.current, { event: "listening", id, channel: "widget" });

  const alternar = () => {
    comando(iframeRef.current, tocando ? "pauseVideo" : "playVideo");
    setTocando(!tocando);
  };

  const buscar = (segundos) => {
    arrastandoRef.current = false;
    setTempo(segundos);
    comando(iframeRef.current, "seekTo", [segundos, true]);
  };

  const mudarVolume = (valor) => {
    setVolume(valor);
    comando(iframeRef.current, "setVolume", [valor]);
    if (valor > 0 && mudo) {
      setMudo(false);
      comando(iframeRef.current, "unMute");
    }
    if (valor === 0 && !mudo) {
      setMudo(true);
      comando(iframeRef.current, "mute");
    }
  };

  const alternarMudo = () => {
    comando(iframeRef.current, mudo ? "unMute" : "mute");
    if (mudo && volume === 0) mudarVolume(50);
    setMudo(!mudo);
  };

  const mudarVelocidade = (valor) => {
    setVelocidade(valor);
    comando(iframeRef.current, "setPlaybackRate", [valor]);
  };

  const telaCheia = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else containerRef.current?.requestFullscreen();
  };

  const capa = `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;

  const src =
    `${ORIGEM_YOUTUBE}/embed/${id}` +
    "?autoplay=1&playsinline=1&rel=0&enablejsapi=1&controls=0&fs=0&disablekb=1&iv_load_policy=3" +
    `&origin=${encodeURIComponent(window.location.origin)}`;

  return (
    <div
      ref={containerRef}
      style={{ position: "absolute", inset: 0, overflow: "hidden", background: "#000" }}
    >
      {!iniciado ? (
        <button
          type="button"
          aria-label={`Assistir ao vídeo: ${titulo}`}
          onClick={() => {
            setIniciado(true);
            setTocando(true);
          }}
          style={estilos.botaoCheio}
        >
          <img
            src={capa}
            alt=""
            loading="lazy"
            onLoad={(e) => {
              if (e.currentTarget.naturalWidth <= 120) {
                e.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
              }
            }}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
          <span aria-hidden="true" style={estilos.centro}>
            <span style={estilos.play}>▶</span>
          </span>
        </button>
      ) : (
        <>
          <iframe
            ref={iframeRef}
            src={src}
            title={titulo}
            allow="autoplay; encrypted-media"
            tabIndex={-1}
            onLoad={escutar}
            style={{
              position: "absolute",
              left: 0,
              top: -90,
              width: "100%",
              height: "calc(100% + 180px)",
              border: 0,
              borderRadius: 0,
            }}
          />
          <button
            type="button"
            aria-label={`${tocando ? "Pausar" : "Continuar"} o vídeo: ${titulo}`}
            onClick={alternar}
            style={estilos.botaoCheio}
          >
            {!tocando && (
              <span aria-hidden="true" style={estilos.centro}>
                <span style={estilos.play}>▶</span>
              </span>
            )}
          </button>
          <div style={estilos.barra}>
            <button
              type="button"
              aria-label={tocando ? "Pausar" : "Continuar"}
              onClick={alternar}
              style={estilos.botaoBarra}
            >
              {tocando ? "❚❚" : "▶"}
            </button>
            <input
              type="range"
              aria-label="Posição do vídeo"
              min={0}
              max={duracao || 0}
              step={1}
              value={Math.min(tempo, duracao || 0)}
              disabled={!duracao}
              onPointerDown={() => {
                arrastandoRef.current = true;
              }}
              onChange={(e) => {
                arrastandoRef.current = true;
                setTempo(Number(e.target.value));
              }}
              onPointerUp={(e) => buscar(Number(e.currentTarget.value))}
              onKeyUp={(e) => buscar(Number(e.currentTarget.value))}
              style={{ flex: 1, minWidth: 0, accentColor: "#fff", cursor: "pointer" }}
            />
            <span style={estilos.tempo}>
              {formatarTempo(tempo)} / {formatarTempo(duracao)}
            </span>
            <button
              type="button"
              aria-label={mudo || volume === 0 ? "Ligar o som" : "Tirar o som"}
              onClick={alternarMudo}
              style={estilos.botaoBarra}
            >
              <IconeSom mudo={mudo || volume === 0} />
            </button>
            <input
              type="range"
              aria-label="Volume"
              min={0}
              max={100}
              step={5}
              value={mudo ? 0 : volume}
              onChange={(e) => mudarVolume(Number(e.target.value))}
              style={estilos.volume}
            />
            <select
              aria-label="Velocidade"
              value={velocidade}
              onChange={(e) => mudarVelocidade(Number(e.target.value))}
              style={estilos.velocidade}
            >
              {VELOCIDADES.map((v) => (
                <option key={v} value={v} style={{ color: "#000" }}>
                  {v === 1 ? "Normal" : `${String(v).replace(".", ",")}x`}
                </option>
              ))}
            </select>
            {telaCheiaDisponivel && (
              <button
                type="button"
                aria-label="Ampliar o vídeo"
                title="Ampliar"
                onClick={telaCheia}
                style={estilos.botaoBarra}
              >
                ⛶
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const estilos = {
  botaoCheio: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    padding: 0,
    border: 0,
    background: "transparent",
    cursor: "pointer",
    display: "block",
  },
  centro: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  play: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "rgba(15,26,30,.72)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    lineHeight: 1,
  },
  barra: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    padding: "6px 10px",
    background: "linear-gradient(transparent, rgba(0,0,0,.75))",
    color: "#fff",
  },
  botaoBarra: {
    flex: "none",
    width: 32,
    height: 32,
    padding: 0,
    border: 0,
    borderRadius: 6,
    background: "transparent",
    color: "#fff",
    fontSize: 16,
    lineHeight: 1,
    cursor: "pointer",
  },
  volume: {
    flex: "none",
    width: 70,
    accentColor: "#fff",
    cursor: "pointer",
  },
  velocidade: {
    flex: "none",
    background: "rgba(0,0,0,.4)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,.4)",
    borderRadius: 6,
    fontSize: 12,
    padding: "2px 4px",
    cursor: "pointer",
  },
  tempo: {
    flex: "none",
    fontSize: 12,
    fontVariantNumeric: "tabular-nums",
  },
};
